// imagepasteResolve — build a `whiteboard.imagepaste` resolver for timelapse
// replay (vandalism-recovery-ui-2026-09-27.md「タイムラプスの imagepaste 再生
// として先行実装する」節, fable-advisor レビュー済み).
//
// `timelapseReplay.ts` treats `whiteboard.imagepaste` as an opaque "give me
// the decoded pixels for this event, or tell me you can't" callback
// (`ResolveImagepasteFn`) — this module is the one production implementation
// of that callback, shared by BOTH the finished-video pipeline (`encodeJob.ts`,
// worker-hosted) and the edit-screen range preview (`previewFrame.ts`,
// main-thread-hosted) so they never diverge (module header 「片方だけだと
// プレビューと動画がずれる」).
//
// Strategy (§ decisions):
//   - basenames are extracted from every `whiteboard.imagepaste.url` in the
//     supplied event list (= 系譜 seed + 可視範囲 superset — the caller
//     passes whatever event array it is about to replay; scanning the WHOLE
//     array is a safe superset of "seed + visible" without duplicating the
//     seed-range computation `timelapseReplay.ts` already owns).
//   - if `adminToken` is present, ALL unique basenames are re-signed up
//     front in one batched round-trip (`resignImagepasteUrls`) — "admin
//     token が無い (非 owner) ときは endpoint を呼ばない" (§5).
//   - regardless of re-sign outcome, each unique basename's **compressed
//     bytes** (`Blob`) are fetched ONCE, in parallel, right away ("worker
//     開始時に...並列 prefetch") — preferring the re-signed URL, falling back
//     to the ORIGINAL broadcast `url` exactly once when re-sign returned
//     `null` (a recent live-tail paste's original URL may still be valid).
//   - decoded RGBA is produced fresh on every `resolve(ev)` call from the
//     cached Blob and is NOT retained (§ memory: large images × many pastes).
//     `resolve` may be called more than once for the same basename (a
//     restore lineage can silently re-apply the same base range from
//     multiple seed points) — the Blob cache absorbs that without re-fetching.
//
// Decode mirrors the live path (`CanvasDrawingEngine.fetchAndApplyImagepaste`):
// `createImageBitmap(blob)` with no options + `decodeBitmapToRgba` (the same
// primitive, imported directly — see that file's header for why it no longer
// requires OffscreenCanvas, Sentry WEB-5) — so a replayed paste lands on the
// same pixels the live canvas / server snapshot would have shown.

import { decodeRgbaPng, type RegionImage } from '@oekaki/drawing-core';
import type { Logger } from '../../application/ports/Logger.js';
import { decodeBitmapToRgba } from '../canvas2d/rasterCanvas.js';
import { type FetchFn, parseImagepasteBasename, resignImagepasteUrls } from './imagepasteUrls.js';

/** Decoded imagepaste pixels — natural (decoded) size, never resized to `ev.width/height`. */
export interface ImagepasteResolveResult {
  readonly rgba: Uint8ClampedArray;
  readonly width: number;
  readonly height: number;
}

/**
 * What `resolve()` needs from an event to resolve its pixels. `url` is
 * optional so a heterogeneous `StrokeLogEvent[]` (only some variants carry
 * a `url`) can be passed directly to `events` below without a pre-filter —
 * `createImagepasteResolver` skips anything without one.
 */
export interface ImagepasteResolveEvent {
  /** Discriminant — required so TS doesn't flag this as a "weak type" (every field optional) against a heterogeneous `StrokeLogEvent[]` that has no `url` at all on most variants. */
  readonly t: string;
  readonly url?: string;
  /** `whiteboard.region` の置く画像 (各 item の `url`)。 */
  readonly items?: ReadonlyArray<{ readonly url?: string }>;
}

/**
 * `whiteboard.region` の置く画像を全 item ぶん (items と同じ順) 返す。1 枚でも取れなければ
 * null (部分成功なし)。PNG は純 JS (`decodeRgbaPng`) で読み、canvas を通さない。
 */
export type ResolveRegionFn = (ev: {
  readonly items: ReadonlyArray<{ readonly url: string }>;
}) => Promise<RegionImage[] | null>;

/**
 * 画像貼り付けの resolver。`.region` に範囲の移動・コピー用の resolver が付く
 * (同じ再署名 + Blob キャッシュを共有。既存の `resolveImagepaste` オプションを
 * 通すだけで region も解決でき、タイムライン側の受け渡しを増やさないための形)。
 */
export type ResolveImagepasteFn = ((
  ev: ImagepasteResolveEvent,
) => Promise<ImagepasteResolveResult | null>) & { readonly region?: ResolveRegionFn };

export interface ImagepasteResolver {
  readonly resolve: ResolveImagepasteFn;
}

// Re-exported so callers only need one module's `FetchFn` for both the
// blob GET and the resign POST (this resolver forwards it to
// `resignImagepasteUrls` verbatim).
export type { FetchFn };

const defaultFetchFn: FetchFn = (input, init) => {
  const f = globalThis.fetch as
    | ((url: string, init?: RequestInit) => Promise<Response>)
    | undefined;
  if (f === undefined) throw new Error('globalThis.fetch is not available');
  return f.call(globalThis, input, init as RequestInit | undefined);
};

export type CreateImageBitmapFn = (blob: Blob) => Promise<ImageBitmap>;

const defaultCreateImageBitmapFn: CreateImageBitmapFn = (blob) => {
  const f = globalThis.createImageBitmap as ((b: Blob) => Promise<ImageBitmap>) | undefined;
  if (f === undefined) {
    return Promise.reject(new Error('globalThis.createImageBitmap is not available'));
  }
  return f.call(globalThis, blob);
};

const noopLogger: Logger = {
  debug: () => undefined,
  info: () => undefined,
  warn: () => undefined,
  error: () => undefined,
};

export interface CreateImagepasteResolverOptions {
  readonly dispatcherUrl: string;
  readonly roomId: string;
  /**
   * Owner `admin_token`. Omit (or empty string) to skip the re-sign
   * round-trip entirely — non-owners fall straight to the "try the original
   * url once" path (§5「admin token が無いときは endpoint を呼ばない」).
   */
  readonly adminToken?: string;
  /**
   * Every event the caller is about to replay (or a safe superset — see
   * module header). Only `whiteboard.imagepaste` entries matter; anything
   * else is ignored.
   */
  readonly events: readonly ImagepasteResolveEvent[];
  readonly fetchFn?: FetchFn;
  readonly createImageBitmapFn?: CreateImageBitmapFn;
  readonly decodeBitmapFn?: (bitmap: ImageBitmap) => ImagepasteResolveResult;
  readonly logger?: Logger;
  readonly signal?: AbortSignal;
}

/**
 * Build a resolver. Construction is synchronous; the re-sign + prefetch
 * round-trips are kicked off immediately (not lazily on first `resolve`) and
 * memoized in a single internal promise every `resolve` call awaits — so
 * calling this once per job/preview-session and reusing `.resolve` is the
 * intended usage (re-constructing per event would re-fetch nothing extra
 * thanks to the Blob cache, but would re-issue the re-sign round-trip).
 */
export const createImagepasteResolver = (
  options: CreateImagepasteResolverOptions,
): ImagepasteResolver => {
  const fetchFn = options.fetchFn ?? defaultFetchFn;
  const createImageBitmapFn = options.createImageBitmapFn ?? defaultCreateImageBitmapFn;
  const decodeBitmapFn = options.decodeBitmapFn ?? decodeBitmapToRgba;
  const logger = options.logger ?? noopLogger;

  // One representative original url per unique basename (module header:
  // basenames are practically unique per real paste — the >1 case is the
  // SAME event being silently re-applied from multiple seed/lineage points,
  // so "representative" is exact, not an approximation).
  const originalUrlByBasename = new Map<string, string>();
  const collect = (url: string | undefined): void => {
    if (url === undefined) return; // non-imagepaste event (heterogeneous StrokeLogEvent[])
    const basename = parseImagepasteBasename(url);
    if (basename === null) return;
    if (!originalUrlByBasename.has(basename)) originalUrlByBasename.set(basename, url);
  };
  for (const ev of options.events) {
    collect(ev.url);
    for (const it of ev.items ?? []) collect(it.url); // whiteboard.region
  }
  const basenames = Array.from(originalUrlByBasename.keys());

  const blobCache = new Map<string, Blob | null>();

  const fetchInit = options.signal !== undefined ? { signal: options.signal } : {};

  const fetchBlob = async (targetUrl: string): Promise<Blob | null> => {
    try {
      const res = await fetchFn(targetUrl, fetchInit);
      if (!res.ok) {
        logger.warn('imagepasteResolve: fetch non-ok', { status: res.status });
        return null;
      }
      return await res.blob();
    } catch (cause) {
      logger.warn('imagepasteResolve: fetch failed', {
        err: cause instanceof Error ? cause.message : String(cause),
      });
      return null;
    }
  };

  // Kicked off eagerly at construction time (§「worker 開始時に...prefetch」),
  // memoized so every `resolve()` call just awaits the same in-flight work.
  const ready: Promise<void> = (async () => {
    if (basenames.length === 0) return;
    const resigned =
      options.adminToken !== undefined && options.adminToken.length > 0
        ? await resignImagepasteUrls(
            {
              dispatcherUrl: options.dispatcherUrl,
              roomId: options.roomId,
              names: basenames,
              adminToken: options.adminToken,
              ...(options.signal !== undefined ? { signal: options.signal } : {}),
            },
            { fetchFn, logger },
          )
        : new Map<string, string | null>();
    await Promise.all(
      basenames.map(async (basename) => {
        const original = originalUrlByBasename.get(basename);
        if (original === undefined) return; // unreachable (basenames derived from the map's own keys)
        const resignedUrl = resigned.get(basename) ?? null;
        // 「再署名が null の画像は元の URL で 1 回だけ試す」— single attempt,
        // no retry chain: prefer the fresh signature, else the original.
        const targetUrl = resignedUrl ?? original;
        blobCache.set(basename, await fetchBlob(targetUrl));
      }),
    );
  })();

  const resolveImagepasteOnly = async (
    ev: ImagepasteResolveEvent,
  ): Promise<ImagepasteResolveResult | null> => {
    if (ev.url === undefined) return null;
    const basename = parseImagepasteBasename(ev.url);
    if (basename === null) return null;
    await ready;
    const blob = blobCache.get(basename) ?? null;
    if (blob === null) return null;
    let bitmap: ImageBitmap;
    try {
      bitmap = await createImageBitmapFn(blob);
    } catch (cause) {
      logger.warn('imagepasteResolve: createImageBitmap failed', {
        err: cause instanceof Error ? cause.message : String(cause),
      });
      return null;
    }
    try {
      // Natural (decoded) size — NEVER resized to the broadcast's
      // display width/height (module header; matches the live path and
      // `EngineCore.applyImagepaste`, which paints at the RGBA's own dims).
      return decodeBitmapFn(bitmap);
    } catch (cause) {
      logger.warn('imagepasteResolve: decode failed', {
        err: cause instanceof Error ? cause.message : String(cause),
      });
      return null;
    } finally {
      try {
        bitmap.close?.();
      } catch {
        // ignore
      }
    }
  };

  const resolveRegion: ResolveRegionFn = async (ev) => {
    await ready;
    const out: RegionImage[] = [];
    for (const item of ev.items) {
      const basename = parseImagepasteBasename(item.url);
      const blob = basename === null ? null : (blobCache.get(basename) ?? null);
      if (blob === null) return null;
      try {
        out.push(decodeRgbaPng(new Uint8Array(await blob.arrayBuffer())));
      } catch (cause) {
        logger.warn('imagepasteResolve: region decode failed', {
          err: cause instanceof Error ? cause.message : String(cause),
        });
        return null;
      }
    }
    return out;
  };

  const resolve: ResolveImagepasteFn = Object.assign(resolveImagepasteOnly, {
    region: resolveRegion,
  });
  return { resolve };
};
